<template>
  <div class="hstack hr-main">
    <div class="d-flex align-items-start gap-3 flex-fill">
      <h1 class="fs-4 fw-normal mb-0">{{ t('title') }}</h1>
      <button
        type="button"
        v-if="userStore.can('himik') && !homePage"
        @click.prevent="$router.push({ name: 'destructions.item' })"
        class="btn btn-secondary"
      >
        <div class="text-nowrap"><i class="bi bi-plus me-1"></i>{{ t('createRequest') }}</div>
      </button>
      <ExportButton
        v-if="!modalstack && !homePage"
        model-name="destructions"
        :columns="baseColumns"
        :filters="query"
      />
    </div>
  </div>

  <Grid
    v-model="lm"
    :total="model.total_count"
    :columns="gridColumns"
    :data="model.data"
    list-name="destructions"
    @click:row="onRowClick"
    :pagination="pagination"
    :page-size-options="pageSizeOptions"
    :persist-key="persistKey"
  >
    <template #cell="{ row, col }">
      <template v-if="col.title === 'actions'">
        <template v-if="modalstack">
          <div
            class="hstack gap-1"
            @click.stop
          >
            <RouterLink
              active-class=""
              :to="{ name: 'destructions.item', params: { id: row.id } }"
              target="_blank"
              class="btn btn-success btn-sm rounded-circle p-0 px-1"
              :title="t('openNewTab')"
            >
              <i class="bi bi-box-arrow-up-right"></i>
            </RouterLink>
            <button
              class="btn btn-success btn-sm rounded-circle p-0 px-1"
              type="button"
              @click="$emit('modalstack:close', row)"
              :title="t('select')"
            >
              <i class="bi bi-check"></i>
            </button>
          </div>
        </template>
      </template>
      <template v-else-if="col.title === 'writeoff_signed'">
        <div @click.stop>
          <WriteoffCell
            :row="row.uploads"
            :destruction-id="row.id"
          />
        </div>
      </template>
      <template v-else-if="col.getter">
        {{ col.getter(row) }}
      </template>
      <template v-else-if="col.path">
        <template v-if="col.date && typeof _result(row, col.path) === 'string'">
          {{ commons.dateLocal(_result(row, col.path)) }}
        </template>
        <template v-else>
          {{ pathLabel(row, col.path, _result(row, col.path), locale) }}
        </template>
      </template>
      <template v-else-if="col.date && row[col.title]">
        {{ commons.dateLocal(row[col.title]) }}
      </template>
      <template v-else>
        {{ row[col.title] }}
      </template>
    </template>
  </Grid>
</template>

<script setup>
import { computed, inject, provide, ref, shallowRef, watch } from 'vue'
import { result as _result } from 'lodash-es'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { commons } from '@/lib/transformer'
import { useApi } from '@/composables/api'
import { useUserStore } from '@/stores/user'
import { useConfigsStore } from '@/stores/configs'
import Grid from '@/components/list/grid.vue'
import WriteoffCell from '@/components/writeoffCell.vue'
import ExportButton from '@/components/exportButton.vue'
import { pathLabel, namedEntityLabel } from '@/lib/dropdownLabel.js'
import { useI18n } from 'vue-i18n'

const { config } = storeToRefs(useConfigsStore())
const { t, locale } = useI18n()

const emits = defineEmits(['list-query', 'modalstack:close'])
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: true,
  },
  homePage: {
    type: Boolean,
    default: false,
  },
  excludeDestructed: {
    type: Boolean,
    default: false,
  },
  initial: {
    type: Object,
    default: () => ({}),
  },
  pageSizeOptions: {
    type: Array,
    default: undefined,
  },
  persistKey: {
    type: String,
    default: '',
  },
})

const router = useRouter()
function onRowClick(row) {
  if (props.modalstack) {
    emits('modalstack:close', row)
    return
  }
  router.push({ name: 'destructions.item', params: { id: row.id } })
}

const lm = ref(props.initial ?? {})

const query = computed(() => ({
  ...lm.value,
  exclude_destructed: props.excludeDestructed,
}))

const { model, refresh } = useApi({
  url: 'destructions',
  data: { data: [] },
  watch: query,
  query: query,
  pick: pickDestructions,
})

function formatActLabel(upload, numField, dateField) {
  if (!upload) return null
  const num = upload.metadata?.[numField]
  const dt = upload.metadata?.[dateField]
  if (num && dt) {
    return t('actNumberWithDate', { number: num, date: commons.dateLocal(dt, 'dd.MM.yyyy') })
  } else if (num) {
    return t('actNumberOnly', { number: num })
  } else if (dt) {
    return commons.dateLocal(dt, 'dd.MM.yyyy')
  }
  return null
}

function pickDestructions(d) {
  if (d.data && Array.isArray(d.data)) {
    d.data = d.data.map(item => {
      const result = { ...item }
      const uploads = item.uploads || []
      const destructionActUpload = uploads.find(u => u.division === 'destruction_act')
      result.destruction_act = formatActLabel(destructionActUpload, 'num_act', 'date_act')
      const writeoffSignedUpload = uploads.find(u => u.division === 'writeoff_signed')
      result.writeoff_signed = formatActLabel(writeoffSignedUpload, 'num_wo', 'date_wo')
      if (item.activity) {
        result.activity_caption =
          namedEntityLabel(item.activity, locale.value) ||
          item.activity.name ||
          t('activityFallback', { id: item.activity.id })
      }
      return result
    })
  }
  return d
}

provide('model', model)
provide('refresh', refresh)

const userStore = useUserStore()
const fullscreen = inject('fullscreen', ref(false))

const baseColumns = computed(() => [
  { title: 'id', label: t('requestNumber'), width: '120px' },
  {
    title: 'date_created',
    label: t('catalog.fields.common.date_created'),
    path: 'date_created',
    date: true,
    width: '160px',
  },
  {
    title: 'assign_user',
    label: t('responsible'),
    path: 'assign_user.username',
    width: '150px',
    export_path: 'assign_user.fio|assign_user.username',
    getter: o => o.assign_user?.fio || o.assign_user?.username,
  },
  { title: 'status', label: t('catalog.fields.common.status'), path: 'status.value', width: '100px' },
  {
    title: 'activity',
    label: t('activity'),
    path: 'activity.name',
    export_path: 'activity_caption',
    getter: o => o.activity_caption || '—',
  },
  {
    title: 'destruction_act',
    label: t('destructionAct'),
    filter: false,
    export_path: '_destruction_act',
  },
  {
    title: 'writeoff_signed',
    label: t('signedWriteoffAct'),
    filter: false,
    export_path: '_writeoff_signed',
  },
])

const gridColumns = shallowRef([])
function updateGridColumns() {
  const cols = baseColumns.value.map(c => {
    const copy = { ...c }
    if (fullscreen?.value) delete copy.width
    return copy
  })
  if (props.modalstack) {
    cols.push({ title: 'actions', label: t('actions'), static: true })
  }
  gridColumns.value = cols
}
watch([fullscreen, () => props.modalstack, locale, baseColumns], updateGridColumns, { immediate: true })

provide(
  'th-cfg',
  computed(() => ({
    ...config.value?.destructions,
    // фильтр статуса по key + подписи value_en
    'io_status.value': true,
  }))
)

watch(lm, v => {
  emits('list-query', v)
})
</script>

<i18n>
{
  "ru": { "title": "Запросы на уничтожение", "createRequest": "Создать новый запрос", "openNewTab": "Открыть в новой вкладке", "select": "Выбрать", "activityFallback": "Активность #{id}", "requestNumber": "Номер запроса", "responsible": "Ответственный", "activity": "Активность", "destructionAct": "Акт уничтожения", "signedWriteoffAct": "Подписанный акт списания ТМЦ", "actions": "Действия", "actNumberWithDate": "№{number} от {date}", "actNumberOnly": "№{number}" },
  "en": { "title": "Destruction requests", "createRequest": "Create new request", "openNewTab": "Open in new tab", "select": "Select", "activityFallback": "Activity #{id}", "requestNumber": "Request No.", "responsible": "Responsible person", "activity": "Activity", "destructionAct": "Destruction act", "signedWriteoffAct": "Signed inventory write-off act", "actions": "Actions", "actNumberWithDate": "No. {number} dated {date}", "actNumberOnly": "No. {number}" }
}
</i18n>
